import React, { Fragment, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from './LoadingSpinner';
import InfoBars from './InfoBars';
import { ReactComponent as SuccessIcon } from '../content/icons/Icon-Success.svg';
import PurchasedItemSummary from './PurchasedItemSummary';
import SetupContext from './../contexts/SetupContext';
import useScreenSize from '../hooks/useScreenSize'
import VoucherProcessingMessage from './VoucherProcessingMessage';
import VoucherService from './../services/VoucherService';

const WebhookPollingPage = (props) => {
    const { intl, microsite, location, getSetupContext } = props;
    const setupContext = useContext(SetupContext);

    WebhookPollingPage.propTypes = {
        location: PropTypes.object.isRequired,
        microsite: PropTypes.string.isRequired
    };

    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [hasCreatedVouchers, setHasCreatedVouchers] = useState(false);
    const [isCreatingVouchers, setIsCreatingVouchers] = useState(false);
    const [billingDetails, setBillingDetails] = useState(null);
    const [totalPrice, setTotalPrice] = useState(null);
    const [soldVouchers, setSoldVouchers] = useState(null);
    const [infoBars, setInfoBars] = useState([]);
    const { isMobileView } = useScreenSize();

    const calculateTotalPrice = (vouchers) => {
        if (!vouchers || vouchers.length === 0) return 0;
        
        const total = vouchers.reduce((sum, voucher) => {
            return sum + (voucher.unitCost * voucher.quantity);
        }, 0);
        
        return total.toFixed(2);
    };

    useEffect(() => {
        if (!setupContext.restaurantName && microsite) {
            getSetupContext(microsite);
        } else {
            setIsCreatingVouchers(true);

            const params = queryString.parse(location.search);
            let checkoutSessionId = params.session_id;

            // For GuestPay, session_id won't be in the URL - retrieve from sessionStorage
            if (!checkoutSessionId) {
                checkoutSessionId = window.sessionStorage.getItem('guestpay_pending_session');
            }

            if (checkoutSessionId) {
                const pollInterval = setInterval(() => {
                    VoucherService.getVoucherOrderIfFulfilled(microsite, checkoutSessionId)
                        .then(response => {
                            if (response && Array.isArray(response.vouchersSold) && response.vouchersSold.length > 0) {
                                clearInterval(pollInterval);
                                setSoldVouchers(response.vouchersSold);
                                setBillingDetails(response.billingDetails);
                                
                                let computedTotalPrice;
                                try {
                                    const storedStateString = window.sessionStorage.getItem(checkoutSessionId);
                                    const storedState = storedStateString ? JSON.parse(storedStateString) : null;
                                    computedTotalPrice = storedState?.totalCartPrice ?? calculateTotalPrice(response.vouchersSold);
                                } catch (error) {
                                    computedTotalPrice = calculateTotalPrice(response.vouchersSold);
                                }
                                
                                setTotalPrice(computedTotalPrice);
                                setHasCreatedVouchers(true);
                                setIsCreatingVouchers(false);
                            }
                        })
                        .catch((error) => {
                            clearInterval(pollInterval);
                            setIsCreatingVouchers(false);
                            setShowErrorMessage(true);
                        });
                }, 2000);

                return () => clearInterval(pollInterval);
            } else {
                setIsCreatingVouchers(false);
                setShowErrorMessage(true);
            }
        }
    }, [setupContext, microsite, location.search, getSetupContext]);

    if (hasCreatedVouchers) {
        return <Fragment>
            <InfoBars infoBars={infoBars} setInfoBars={setInfoBars} />
            {hasCreatedVouchers && billingDetails &&
                <Fragment>
                    <div className={isMobileView ? "success-page-wrapper mobile" : "success-page-wrapper"}>
                        <div className="confirmation-heading">
                            <div className="progress-icon-container success-icon">
                                <SuccessIcon />
                            </div>
                            <FormattedMessage id={"Common.ThanksForYourPurchase"} tagName={"h2"} />
                            <FormattedMessage id={"Common.PurchaseConfirmation"} values={{ email: billingDetails.email }} />
                        </div>
                        <div className="purchased-items-container">
                            {soldVouchers && soldVouchers.map(ci =>
                                <PurchasedItemSummary
                                    key={ci.voucherId}
                                    purchasedVoucher={ci}
                                    isMobileView={isMobileView}
                                />
                            )}
                        </div>
                        <div className="total-paid-wrapper">
                            <div className="fw-bold ms-auto total-price text-uppercase">
                                <h6><FormattedMessage id="Vouchers.TotalPaid" /> {setupContext.currencySymbol}{totalPrice}</h6>
                            </div>
                        </div>
                    </div>
                </Fragment>
            }
        </Fragment>
    } else if (showErrorMessage)  {
        return <VoucherProcessingMessage
            headerText = {intl.formatMessage({ id: 'Vouchers.VoucherFailureMessageHeading' })}
            bodyText={{
                mainBodyText: intl.formatMessage({ id: 'Vouchers.VoucherFailureMessageBody' })
            }}></VoucherProcessingMessage>;
    } else if (!hasCreatedVouchers && !showErrorMessage && isCreatingVouchers) {
        return <Fragment><VoucherProcessingMessage headerText={intl.formatMessage({ id: 'Vouchers.VoucherProcessingHeaderMessage' })}
            bodyText={{
                mainBodyText: intl.formatMessage({ id: 'Vouchers.VoucherProcessingBodyMainMessage' }),
                secondaryBodyText: intl.formatMessage({ id: 'Vouchers.VoucherProcessingBodySecondaryMessage' }),
            }}></VoucherProcessingMessage><LoadingSpinner loadingText={intl.formatMessage({ id: 'Common.CreatingVouchers' })} /></Fragment>;
    } else {
        return null;
    }
}

export default injectIntl(WebhookPollingPage);
